// WaveTalk web client — browser entry. Wires the Joana chat shell to the real
// browser: a DOM renderer painting into #app, the native WebSocket for the
// realtime channel, and the global fetch for REST. This is the first-party chat
// surface running entirely on Joana (ADR-0011) — no isolate. It ALSO embeds the
// mini-app host: untrusted mini-apps run sandboxed in a Web Worker (the WASM
// isolate) and paint into the #miniapp-layer overlay, reaching platform features
// only through the trusted capability handlers below.
//
// API + socket origins come from window globals the host page sets (so the same
// bundle points at dev/staging/prod), defaulting to the page origin.

import { ApiClient } from "../joana/shell/api-client.ts";
import { SignalClient } from "../joana/signal/signal-client.ts";
import { createSignalDeviceLink } from "../joana/signal/device-link.ts";
import { IdbMessageStorage } from "../joana/signal/idb-store.ts";
import { Socket, type WebSocketLike } from "../joana/shell/phoenix-channel.ts";
import { Shell } from "../joana/shell/shell.ts";
import { themeAttribute } from "../joana/shell/appearance.ts";
import { ShellStore } from "../joana/shell/store.ts";
import { MiniAppHost } from "../joana/shell/miniapp-host.ts";
import { SAMPLE_MINIAPP } from "../joana/shell/sample-miniapp.ts";
import type { MiniAppPackage, PaymentIntentResult } from "../joana/shell/types.ts";
import { PaymentSheetController, parsePaymentIntent } from "../joana/shell/payment-sheet.ts";
import { createLocationHandler } from "../joana/shell/device-location.ts";
import { domRendererFactory, workerClientTransport } from "../joana/worker/web-transport.ts";
import type { DomDocument, DomElement } from "../joana/render/dom-renderer.ts";
import { MiniAppOverlay } from "./miniapp-overlay.ts";
import { CameraSheet, type CaptureOptions } from "./camera-sheet.ts";
import { AvatarEditorSheet, pickFromGrid, askText } from "./avatar-editor-sheet.ts";
import { AvatarLoader } from "../joana/shell/avatar-loader.ts";
import { StatusMediaLoader } from "../joana/shell/status-media-loader.ts";

interface Win {
  __API_BASE__?: string;
  __WS_BASE__?: string;
  /**
   * ADR-0033 D9 — which bundle this is, for the server's version floor.
   *
   * Written into `config.js` by the deploy, exactly like `__API_BASE__`, and
   * ABSENT in dev. Absent means 0, which is the lowest and so is refused by
   * any floor that refuses anything — the safe direction for a claim we cannot
   * make. A dev bundle is not meant to survive a floor being set against it.
   */
  __WEB_BUILD__?: number;
  location: { origin: string; reload?: () => void };
}
const win = globalThis as unknown as Win;

const apiBase = win.__API_BASE__ ?? win.location.origin;
/** ADR-0033 D9 — this bundle's identity on every request and on the socket. */
const clientBuild = { platform: "web", build: Number(win.__WEB_BUILD__ ?? 0) };
const wsBase =
  win.__WS_BASE__ ?? win.location.origin.replace(/^http/, "ws") + "/socket";

const mount = document.getElementById("app")!;
const miniAppLayer = document.getElementById("miniapp-layer")!;
const paymentLayer = document.getElementById("payment-layer")!;
const cameraLayer = document.getElementById("camera-layer")!;

const store = new ShellStore();
const api = new ApiClient({ baseUrl: apiBase, clientBuild });

const TOKEN_KEY = "wavetalk.refresh_token";

// --- trusted payment sheet -------------------------------------------------
// Host-owned modal for a mini-app's payments.intent. Rendered THROUGH the Joana
// render layer into #payment-layer; the controller never touches the DOM (it
// only toggles the layer's visibility through this callback).
const paymentSheet = new PaymentSheetController(
  domRendererFactory(
    document as unknown as DomDocument,
    paymentLayer as unknown as DomElement,
  ),
  (visible) => {
    paymentLayer.style.display = visible ? "flex" : "none";
  },
);

// --- trusted device capabilities -------------------------------------------
// camera.capture — host-owned capture sheet (live preview never reaches the app).
const cameraSheet = new CameraSheet(cameraLayer);
// location.coarse/.fine — answered via the browser Geolocation API (one handler,
// the op picks the precision); the browser owns the permission prompt.
const locationHandler = createLocationHandler();

// --- embedded mini-app host ------------------------------------------------

const overlay = new MiniAppOverlay(miniAppLayer, () => closeMiniApp());

const miniAppHost = new MiniAppHost({
  // A fresh Web Worker per open — the WASM isolate runs untrusted logic off the
  // DOM thread. terminate() on close kills it deterministically.
  createSession: () => {
    const worker = new Worker(new URL("./worker.js", import.meta.url), { type: "module" });
    return { transport: workerClientTransport(worker), dispose: () => worker.terminate() };
  },
  // The mini-app's view paints into the overlay's sandboxed viewport.
  contentRenderer: domRendererFactory(
    document as unknown as DomDocument,
    overlay.contentEl as unknown as DomElement,
  ),
  // Trusted main-thread capability handlers: the SHELL presents the UI (a toast
  // here) on the mini-app's behalf. The mini-app never touches the DOM/OS.
  capabilities: {
    "share.sheet": (_op, args) => {
      overlay.toast(`Shared: ${String((args as { text?: string }).text ?? "")}`);
      return { shared: true };
    },
    "notifications.local": (_op, args) => {
      const a = args as { title?: string; body?: string };
      const t = hostTranslator();
      overlay.toast(a.title ?? a.body ?? t("Notification"));
      return { shown: true };
    },
    // payments.intent — the app requests; the SHELL confirms + charges. We
    // present the trusted sheet, and on approve charge Phoenix /payments/initiate
    // → Rust. The mini-app gets back only the result, never the provider/phone.
    "payments.intent": async (_op, args): Promise<PaymentIntentResult> => {
      const request = parsePaymentIntent(args as Record<string, unknown>);
      return paymentSheet.present({
        appTitle: store.state.miniApp.title || "Mini-app",
        request,
        charge: async ({ provider, phone }) => {
          const r = await api.initiatePayment({
            amount: request.amount,
            currency: request.currency,
            provider,
            phone_number: phone,
            description: request.description,
          });
          return {
            status: r.status,
            amount: request.amount,
            currency: request.currency,
            reference: r.provider_ref ?? r.transaction_id ?? null,
            provider,
          };
        },
      });
    },
    // camera.capture — the SHELL runs the camera; the app gets only the still.
    "camera.capture": (_op, args) => cameraSheet.capture(args as CaptureOptions),
    // location.coarse / location.fine — same handler, the op picks the precision.
    "location.coarse": locationHandler,
    "location.fine": locationHandler,
  },
  onAudit: (e) => console.log("[miniapp-audit]", e.appId, e.capability, e.decision, e.detail ?? ""),
  onStatus: (status, error) => {
    store.setMiniAppStatus(status, error);
    overlay.setStatus(status, error);
  },
});

// Resolve a launcher app_id to a package: the built-in sample locally, any other
// id fetched + validated from the registry (GET /miniapps/:id/bundle).
function resolvePackage(appId: string): Promise<MiniAppPackage> {
  if (appId === "sample") return Promise.resolve(SAMPLE_MINIAPP);
  return api.fetchMiniAppPackage(appId);
}

function closeMiniApp(): void {
  miniAppHost.close();
  overlay.hide();
  store.closeMiniApp();
}

// --- shell -----------------------------------------------------------------

const avatarLoader = new AvatarLoader(
  apiBase,
  () => api.bearerToken,
  () => store.bumpAvatars(),
);
// D8: status media, the same arrangement as the avatar loader — a blob: URL
// per (status, rendition), because an <img> cannot carry the Bearer this route
// requires, and the server decides per viewer.
const statusMediaLoader = new StatusMediaLoader(
  apiBase,
  () => api.bearerToken,
  () => store.bumpStatusTick(),
);

const avatarEditor = new AvatarEditorSheet();

// ADR-0027 D4 — this browser's Signal device. Its own worker script, so the
// mini-app isolate and the DOM are not merely unused by it, they are
// unreachable. Building it lazily is tempting and wrong: the identity has to
// exist BEFORE the QR is rendered, which is the first thing the login screen
// does.
const signalLink = createSignalDeviceLink(
  new SignalClient(new Worker(new URL("./signal-worker.js", import.meta.url), { type: "module" })),
  api,
  () => store.state.me?.id ?? null,
  "./sema_signal_ffi.wasm",
  // ADR-0027 D4.6 — where decrypted messages live once this browser has read
  // them. It has to be durable and it has to be here: acking an envelope
  // DELETES the server's copy, and our server holds no plaintext it could
  // re-serve. Opened lazily, so a browser that never becomes a device never
  // creates the database.
  () => IdbMessageStorage.open(),
);

const shell = new Shell({
  store,
  api,
  signal: signalLink,
  avatars: {
    peek: (ref) => avatarLoader.peek({ kind: ref.kind, ownerId: ref.ownerId, version: ref.version, rendition: ref.rendition }),
    forgetMisses: () => avatarLoader.forgetMisses(),
    clear: () => avatarLoader.clear(),
  },
  statusMedia: {
    peek: (ref) => statusMediaLoader.peek(ref),
    sweepExpired: (ids) => statusMediaLoader.sweepExpired(ids),
    forgetMisses: () => statusMediaLoader.forgetMisses(),
    clear: () => statusMediaLoader.clear(),
  },
  pickAvatarPhoto: (v) => avatarEditor.pickAndCrop(v),
  pickFromGrid,
  askText,
  rendererFactory: domRendererFactory(
    document as unknown as DomDocument,
    mount as unknown as DomElement,
  ),
  clientBuild,
  // ADR-0033 D9 — the web's forced update is a fresh BUNDLE, not an install.
  // The shell has no `location`; the host does.
  onReload: () => (globalThis as unknown as { location: { reload(): void } }).location.reload(),
  createSocket: (connectParams) =>
    new Socket(wsBase, {
      // Auth via a fresh single-use ticket per connect (no token in the URL).
      connectParams,
      transport: (url) => new WebSocket(url) as unknown as WebSocketLike,
    }),
  // The one setting that is CSS rather than tree: the generated palette keys
  // off `<html data-appearance>`, and `system` is the ABSENCE of the
  // attribute, not the string.
  onAppearance: (choice) => {
    const attr = themeAttribute(choice);
    if (attr === null) document.documentElement.removeAttribute("data-appearance");
    else document.documentElement.setAttribute("data-appearance", attr);
  },
  // G16 — the chat wallpaper, for the same reason and by the same route: one
  // <style> element, rewritten in place, holding `--wt-wallpaper-canvas` for
  // both appearances. A colour written into the TREE would be whichever
  // appearance was drawn when it was written, because switching the lights
  // runs no render pass.
  onWallpaper: (css) => {
    let el = document.getElementById("wt-wallpaper") as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = "wt-wallpaper";
      document.head.appendChild(el);
    }
    el.textContent = css;
  },
  onRefreshToken: (rt) => {
    try {
      if (rt === null) localStorage.removeItem(TOKEN_KEY);
      else localStorage.setItem(TOKEN_KEY, rt);
    } catch {
      // storage may be unavailable (private mode); session still works in-memory
    }
  },
  // The other half of the line above, and for a long time it did not exist: the
  // refresh token was written here on every sign-in and read back by nothing, so
  // every reload landed on the login screen with the credential that would have
  // restored the session sitting in storage the whole time.
  restoreRefreshToken: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      // Private mode, or storage blocked. No stored session, which is the same
      // as never having had one — the sign-in screen is the right answer.
      return null;
    }
  },
  onOpenMiniApp: async (appId, title) => {
    overlay.show(title);
    try {
      const pkg = await resolvePackage(appId); // may fetch from the registry
      await miniAppHost.open(pkg);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      store.setMiniAppStatus("error", msg);
      overlay.setStatus("error", msg);
    }
  },
  onCloseMiniApp: () => {
    miniAppHost.close();
    overlay.hide();
  },
});

// The three-pane frame's one input (joana/shell/frame.ts). Set BEFORE start()
// so the first paint is already the right shape — a frame that arrived on the
// first resize would flash the wrong layout on every load — and kept in step
// after, because the breakpoint is a live property of the window and not a
// fact about the machine.
const syncViewport = () => store.setViewportWidth(window.innerWidth);
syncViewport();
window.addEventListener("resize", syncViewport);

shell.start();

// Expose for the headless-Chrome E2E to assert against (no behavioral effect).
(globalThis as unknown as { __shellState__?: () => unknown }).__shellState__ = () => store.state;
(globalThis as unknown as { __miniAppOpen__?: () => boolean }).__miniAppOpen__ = () => miniAppHost.isOpen;
